import { useEffect, useState } from "react";
import { Icon } from "./Icon";

const PIN = (import.meta.env.VITE_APP_PIN as string) || "2026";
const STORAGE_KEY = "pm26.pin.ok";

export function PinGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);

  function submit(value: string) {
    if (value === PIN) {
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setInput("");
      setTimeout(() => setError(false), 700);
    }
  }

  useEffect(() => {
    if (input.length === 4) submit(input);
  }, [input]);

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-full flex flex-col items-center justify-center px-8 safe-top safe-bottom">
      <div className="flex items-center gap-2.5 mb-8">
        <span className="w-1 h-5 bg-accent rounded-full" />
        <span className="text-accent text-2xs font-semibold tracking-eyebrow">
          NEXXT · PAL MUNDO
        </span>
      </div>
      <div className="text-zinc-100 text-3xl font-semibold tracking-tight mb-1.5">
        Control Panel
      </div>
      <div className="text-zinc-500 text-sm mb-12 text-center">
        Pal Mundo Festival · 18 juli 2026
      </div>
      <div className="eyebrow mb-4">Voer PIN in</div>
      <div
        className={`flex gap-3 mb-10 ${error ? "animate-pulse" : ""}`}
        aria-live="polite"
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`w-12 h-14 rounded-xl flex items-center justify-center text-xl transition-all duration-200 ease-spring ${
              error
                ? "border border-risk bg-risk/10"
                : i < input.length
                  ? "border border-accent/60 bg-accent/10 shadow-glow"
                  : "border border-white/[0.08] bg-ink-925"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full transition-all duration-300 ease-spring ${
                i < input.length ? "bg-accent scale-100" : "bg-transparent scale-0"
              }`}
            />
          </div>
        ))}
      </div>
      <div className="bezel-shell w-full max-w-xs">
        <div className="bezel-core p-2">
          <div className="grid grid-cols-3 gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <KeypadButton
                key={n}
                onClick={() => setInput((p) => (p.length < 4 ? p + n : p))}
              >
                {n}
              </KeypadButton>
            ))}
            <div />
            <KeypadButton
              onClick={() => setInput((p) => (p.length < 4 ? p + "0" : p))}
            >
              0
            </KeypadButton>
            <KeypadButton
              onClick={() => setInput((p) => p.slice(0, -1))}
              subtle
              ariaLabel="Wis laatste cijfer"
            >
              <Icon.Backspace className="w-5 h-5 mx-auto" />
            </KeypadButton>
          </div>
        </div>
      </div>
      <p className="text-2xs text-zinc-600 mt-8 max-w-xs text-center">
        Verwerker: Prepared · Operator: NEXXT Productions
      </p>
    </div>
  );
}

function KeypadButton({
  children,
  onClick,
  subtle,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  subtle?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className={`h-16 rounded-xl flex items-center justify-center text-2xl font-medium ${
        subtle
          ? "text-zinc-500 hover:text-zinc-200"
          : "text-zinc-100 bg-ink-850 hover:bg-ink-800 border border-white/[0.04]"
      }`}
      style={{
        boxShadow: subtle
          ? undefined
          : "inset 0 1px 0 0 rgba(255,255,255,0.04)",
      }}
    >
      {children}
    </button>
  );
}
